import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { usePageMeta } from '@/hooks/usePageMeta';

const VALUES = [
  {
    icon: 'ri-global-line',
    title: '专注亚洲影视',
    desc: '覆盖中国、日本、韩国、泰国、香港、台湾等地区，从热门新片到经典老剧一站式浏览。',
  },
  {
    icon: 'ri-translate-2',
    title: '全站中文字幕',
    desc: '所有影片均标注中文字幕支持情况，帮助不同语言的观众轻松理解剧情内容。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '内容分类准确',
    desc: '按地区、年份、类型进行多维度分类，配合搜索与筛选，快速定位想看的内容。',
  },
  {
    icon: 'ri-heart-3-line',
    title: '重视用户体验',
    desc: '清爽的界面、流畅的浏览与本地收藏功能，让你随时找回喜欢的作品。',
  },
];

const STATS = [
  { value: '24+', label: '演示影片' },
  { value: '6', label: '覆盖地区' },
  { value: '12', label: '内容类型' },
  { value: '100%', label: '中文字幕' },
];

export default function AboutPage() {
  usePageMeta(
    '关于我们 - 亚洲中文字幕在线观看',
    '了解亚洲中文字幕在线观看的品牌定位、服务宗旨，以及本站的版权声明与免责声明。',
  );

  return (
    <Layout>
      <div className="w-full px-4 md:px-8 py-6 md:py-8">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

        <section className="mt-6 relative rounded-lg overflow-hidden border border-background-200 bg-background-100">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20cinema%20theater%20interior%20at%20night%20with%20rows%20of%20amber%20seats%20and%20glowing%20screen%2C%20deep%20navy%20blue%20tones%20with%20warm%20gold%20lighting%2C%20atmospheric%20cinematic%20wide%20composition%2C%20high%20detail&width=1600&height=500&seq=about-banner&orientation=landscape"
            alt="亚洲中文字幕在线观看品牌介绍"
            title="亚洲中文字幕在线观看 - 品牌介绍"
            className="w-full h-52 md:h-64 object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-950/90 via-background-950/70 to-background-950/40"></div>
          <div className="absolute inset-0 px-6 md:px-10 flex flex-col justify-center">
            <h1 className="font-heading font-black text-2xl md:text-4xl text-background-50">
              关于亚洲中文字幕在线观看
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl leading-7">
              我们相信，好故事不该被语言阻挡。本站致力于为亚洲影视爱好者提供清晰、便捷、分类准确的中文字幕影视浏览体验。
            </p>
          </div>
        </section>

        <section className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
          {STATS.map((stat) => (
            <div
              key={stat.label}
              className="rounded-lg bg-background-100 border border-background-200 p-5 text-center"
            >
              <p className="font-label text-3xl md:text-4xl text-primary-500">{stat.value}</p>
              <p className="mt-1 text-xs md:text-sm text-foreground-500">{stat.label}</p>
            </div>
          ))}
        </section>

        <section className="mt-8">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">
            我们的服务宗旨
          </h2>
          <p className="mt-3 text-sm text-foreground-600 leading-7 max-w-3xl">
            亚洲中文字幕在线观看始终以观众体验为核心，力求让每一位用户都能在简洁清晰的界面中，快速找到想看的中文字幕影视作品，
            并获取准确、完整的影片资料与相关推荐。
          </p>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-5">
            {VALUES.map((value) => (
              <div
                key={value.title}
                className="flex gap-4 rounded-lg bg-background-100 border border-background-200 p-5"
              >
                <span className="w-11 h-11 rounded-md bg-primary-500/15 text-primary-500 flex items-center justify-center shrink-0">
                  <i className={`${value.icon} text-xl`}></i>
                </span>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-950">{value.title}</h3>
                  <p className="mt-1.5 text-sm text-foreground-500 leading-6">{value.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="rounded-lg bg-background-100 border border-background-200 p-6">
            <h2 className="font-heading font-bold text-lg text-foreground-950 flex items-center gap-2">
              <i className="ri-copyright-line text-primary-500"></i>
              版权声明
            </h2>
            <p className="mt-3 text-sm text-foreground-600 leading-7">
              本站为演示性质站点，页面上呈现的所有影视作品名称、剧情简介、演职人员、评分及相关资料均为虚构内容，
              仅用于展示网站的结构、视觉与交互效果，并非真实影视作品信息，如有雷同纯属巧合。
            </p>
            <p className="mt-2 text-sm text-foreground-600 leading-7">
              本站不提供任何视频文件的存储、播放、下载或分发服务，页面中的播放区域仅为界面演示。
            </p>
          </div>
          <div className="rounded-lg bg-background-100 border border-background-200 p-6">
            <h2 className="font-heading font-bold text-lg text-foreground-950 flex items-center gap-2">
              <i className="ri-error-warning-line text-accent-500"></i>
              免责声明
            </h2>
            <p className="mt-3 text-sm text-foreground-600 leading-7">
              本站所有演示内容均不构成任何观看指引或资源推荐。用户应通过正规、合法的授权平台观看影视作品，
              并遵守所在地区的法律法规及版权相关规定。
            </p>
            <p className="mt-2 text-sm text-foreground-600 leading-7">
              若本站演示内容无意中侵犯到您的权益，请通过帮助页的联系方式与我们取得联系，我们将及时处理。
            </p>
          </div>
        </section>

        <section className="mt-8 rounded-lg bg-background-100 border border-background-200 p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-5">
          <div>
            <h2 className="font-heading font-bold text-lg text-foreground-950">
              开启你的亚洲影视之旅
            </h2>
            <p className="mt-2 text-sm text-foreground-500">
              浏览最新上线与热门推荐，找到下一部想看的作品。
            </p>
          </div>
          <div className="flex gap-3">
            <Link
              to="/category/hot"
              className="px-5 h-10 flex items-center gap-2 rounded-md bg-primary-500 text-background-950 text-sm font-medium hover:bg-primary-600 cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-fire-line"></i>
              热门影视
            </Link>
            <Link
              to="/help"
              className="px-5 h-10 flex items-center gap-2 rounded-md border border-background-300 text-foreground-700 text-sm font-medium hover:bg-background-200 cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-question-line"></i>
              常见问题
            </Link>
          </div>
        </section>
      </div>
    </Layout>
  );
}